import React, { Suspense, useEffect } from 'react';
import { Box, CircularProgress, GlobalStyles } from '@mui/material';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { gql, useQuery } from 'urql';
import { useFlashMessageContext } from '../../../contexts/flashMessage';
import { FetchLeagueTypeQuery, FetchLeagueTypeQueryVariables } from '../../../generated/hooks';
import { LeagueType } from '../../../generated/schemas';

// Each league type is its own chunk: a member of one league type never needs the other two types'
// order-matching / auction / pick UI, so there's no reason to ship all three on initial load.
const PickEmLeagueDetail = React.lazy(() => import(/* webpackChunkName: "league-pick-em" */ './PickEmLeagueDetail'));
const StockMarketLeagueDetail = React.lazy(
  () => import(/* webpackChunkName: "league-stock-market" */ './StockMarketLeagueDetail'),
);
const StockMarketSimulationLeagueDetail = React.lazy(
  () => import(/* webpackChunkName: "league-stock-market-simulation" */ './StockMarketSimulationLeagueDetail'),
);

const fetchLeagueType = gql`
  query fetchLeagueType($leagueId: UUID!) {
    league(id: $leagueId) {
      id
      leagueType
      season
      name
    }
  }
`;

function LeagueDetail() {
  const { addFlashMessage } = useFlashMessageContext();
  const {
    params: { leagueId },
  } = useMatch('/league/:leagueId/*');
  const locationState = useLocation()?.state as {
    leagueName: string;
    leagueType: LeagueType;
    season: number;
  };
  const navigate = useNavigate();
  const [{ data, error }] = useQuery<FetchLeagueTypeQuery, FetchLeagueTypeQueryVariables>({
    query: fetchLeagueType,
    variables: { leagueId },
    pause: Boolean(locationState?.leagueType),
  });

  const leagueType = locationState?.leagueType || data?.league?.leagueType;

  useEffect(() => {
    if (data && !data.league && error?.graphQLErrors[0].message === 'League matching query does not exist.') {
      addFlashMessage('warning', `League with ID ${leagueId} does not exist`);
      navigate('/', { replace: true });
    }
  }, [data, error]);

  if (!leagueType) {
    return null;
  }

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#ffffff' } }} />
      <Suspense
        fallback={
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        }
      >
        {leagueType === LeagueType.PickEm && <PickEmLeagueDetail leagueId={leagueId} />}
        {leagueType === LeagueType.StockMarketSimulation && (
          <StockMarketSimulationLeagueDetail leagueId={leagueId} />
        )}
        {leagueType === LeagueType.StockMarket && <StockMarketLeagueDetail leagueId={leagueId} />}
      </Suspense>
    </>
  );
}

export default LeagueDetail;
